import { Link } from 'react-router-dom';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { ArrowUpCircle, Lock } from 'react-feather';
import { Button } from '@/shared/ui/Button';
import { useAuth } from '@/core/auth/useAuth';
import type { PlanUsage } from './platformApi';
import {
  DIMENSION_LABELS,
  parsePlanLimitApiMessage,
  planLimitCap,
  planLimitReachedMessage,
  planLimitUsed,
  registerPlanLimitOpener,
  type PlanLimitBlockPayload,
  type PlanLimitDimension,
} from './planLimits';

interface PlanLimitContextValue {
  openPlanLimit: (payload: PlanLimitBlockPayload) => void;
  openFromApiMessage: (message: string, usage?: PlanUsage | null) => boolean;
}

const PlanLimitContext = createContext<PlanLimitContextValue | null>(null);

export function usePlanLimitModal(): PlanLimitContextValue {
  const ctx = useContext(PlanLimitContext);
  if (!ctx) {
    return {
      openPlanLimit: () => undefined,
      openFromApiMessage: () => false,
    };
  }
  return ctx;
}

function buildPayloadFromUsage(
  usage: PlanUsage,
  dimension: PlanLimitDimension,
  additional = 1,
): PlanLimitBlockPayload | null {
  const cap = planLimitCap(usage, dimension);
  if (cap == null) return null;
  const used = planLimitUsed(usage, dimension);
  if (used + additional <= cap) return null;
  return {
    kind: 'quota',
    dimension,
    cap,
    used,
    plan: usage.plan,
    additional,
  };
}

export function PlanLimitProvider({ children }: { children: ReactNode }) {
  const { role, user } = useAuth();
  const [payload, setPayload] = useState<PlanLimitBlockPayload | null>(null);

  const openPlanLimit = useCallback((next: PlanLimitBlockPayload) => {
    setPayload(next);
  }, []);

  const openFromApiMessage = useCallback(
    (message: string, usage?: PlanUsage | null) => {
      const parsed = parsePlanLimitApiMessage(message);
      if (!parsed) return false;
      if (parsed.kind === 'channel') {
        openPlanLimit({ ...parsed, plan: usage?.plan ?? parsed.plan });
        return true;
      }
      if (usage) {
        const fromUsage = buildPayloadFromUsage(usage, parsed.dimension);
        if (fromUsage) {
          openPlanLimit(fromUsage);
          return true;
        }
      }
      openPlanLimit({ ...parsed, plan: usage?.plan ?? parsed.plan });
      return true;
    },
    [openPlanLimit],
  );

  useEffect(() => registerPlanLimitOpener(openPlanLimit), [openPlanLimit]);

  const value = useMemo(
    () => ({ openPlanLimit, openFromApiMessage }),
    [openPlanLimit, openFromApiMessage],
  );

  return (
    <PlanLimitContext.Provider value={value}>
      {children}
      <PlanUpgradeModal
        payload={payload}
        schoolId={user?.schoolId ?? null}
        role={role}
        onClose={() => setPayload(null)}
      />
    </PlanLimitContext.Provider>
  );
}

function PlanUpgradeModal({
  payload,
  schoolId,
  role,
  onClose,
}: {
  payload: PlanLimitBlockPayload | null;
  schoolId: string | null;
  role: string | null;
  onClose: () => void;
}) {
  if (!payload) return null;

  const isSchoolAdmin = role === 'school_admin';
  const isSuperAdmin = role === 'super_admin';

  const title =
    payload.kind === 'channel' ? 'Channel not on your plan' : 'Plan limit reached';

  const label =
    payload.kind === 'channel'
      ? payload.channel.toUpperCase()
      : DIMENSION_LABELS[payload.dimension];

  const detail =
    payload.kind === 'channel'
      ? `Your current plan does not include the ${payload.channel} channel.`
      : planLimitReachedMessage(payload.dimension, payload.cap);

  const used = payload.kind === 'quota' ? payload.used : null;
  const cap = payload.kind === 'quota' ? payload.cap : null;
  const pct =
    used != null && cap != null && cap > 0
      ? Math.min(100, Math.round((used / cap) * 100))
      : null;

  const upgradeTo = isSuperAdmin
    ? schoolId
      ? `/royal-overseas/schools/${schoolId}`
      : '/royal-overseas/plans'
    : '/admin/settings';

  const upgradeLabel = isSuperAdmin
    ? schoolId
      ? 'Open school billing'
      : 'Manage plans'
    : 'Contact / school settings';

  return (
    <Modal
      isOpen
      toggle={onClose}
      centered
      size="lg"
      className="credora-modal"
      modalClassName="credora-modal"
    >
      <ModalHeader toggle={onClose}>{title}</ModalHeader>
      <ModalBody>
        <div className="text-center px-md-4 py-2">
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
            style={{
              width: 72,
              height: 72,
              background: 'rgba(220, 53, 69, 0.12)',
              color: 'var(--bs-danger)',
            }}
          >
            <Lock size={32} />
          </div>
          <h4 className="fw-semibold mb-2">{label} quota is full</h4>
          <p className="text-muted mb-3 mx-auto" style={{ maxWidth: 440 }}>
            {detail}
          </p>
          {payload.plan ? (
            <p className="small text-uppercase text-muted mb-3">
              Current plan · <strong className="text-body">{payload.plan}</strong>
            </p>
          ) : null}
          {used != null && cap != null ? (
            <div className="text-start mx-auto mb-4" style={{ maxWidth: 440 }}>
              <div className="d-flex justify-content-between small mb-1">
                <span className="text-muted">Usage</span>
                <span className="fw-semibold">
                  {used} / {cap}
                  {pct != null ? ` (${pct}%)` : ''}
                </span>
              </div>
              <div className="progress" style={{ height: 10 }}>
                <div
                  className="progress-bar bg-danger"
                  role="progressbar"
                  style={{ width: `${pct ?? 100}%` }}
                />
              </div>
            </div>
          ) : null}
          <div
            className="rounded-3 text-start p-3 mx-auto mb-2"
            style={{ maxWidth: 440, background: 'var(--bs-tertiary-bg, #f8f9fa)' }}
          >
            <p className="fw-semibold mb-2 d-flex align-items-center gap-2">
              <ArrowUpCircle size={18} />
              Upgrade to continue
            </p>
            <p className="small text-muted mb-0">
              {isSuperAdmin
                ? 'Raise this school’s plan or adjust limits on the tenant profile, then retry the action.'
                : isSchoolAdmin
                  ? 'Contact Royal Overseas to upgrade your subscription, or free seats (remove inactive records) and try again.'
                  : 'Ask your school administrator to upgrade the plan or free capacity, then try again.'}
            </p>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="justify-content-center gap-2 flex-wrap">
        <Button color="secondary" outline onClick={onClose}>
          Not now
        </Button>
        {isSuperAdmin || isSchoolAdmin ? (
          <Button color="primary" tag={Link} to={upgradeTo} onClick={onClose}>
            <ArrowUpCircle size={16} className="me-1" />
            {upgradeLabel}
          </Button>
        ) : null}
      </ModalFooter>
    </Modal>
  );
}
